import { useMemo } from 'react';
import { Tag, Typography, Space, Tooltip, Button } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import { usePaymentStatusRender } from './usePaymentStatusRender';
import { useUserTimestampRender } from '@src/hooks';
import { PAYMENT_STATUS_FILTER_OPTIONS } from '../constants';
import dayjs from 'dayjs';

const { Text } = Typography;

interface UseSalesColumnsProps {
  onCreateTransaction?: (transactionData: {
    transaction_type: 'sell';
    s_id: number;
    amount: number;
    customer_name: string; // Add customer_name to type definition
    type: string; // Add payment type
  }) => void;
  showCreatedAt?: boolean;
}

export const useSalesColumns = (
  props?: UseSalesColumnsProps
): SmartDataTableProps['columns'] => {
  const { onCreateTransaction, showCreatedAt = true } = props || {};
  const renderPaymentStatus = usePaymentStatusRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  // Handle patient info click to redirect to patient details
  const handlePatientInfoClick = (record: any, e: any) => {
    e.stopPropagation();
    // Only redirect if patient object exists and has an id
    if (record.patient && record.patient.id) {
      const url = `${window.location.origin}/patients/${record.patient.id}`;
      window.open(url, '_blank');
    }
  };

  const handleCreateTransaction = (record: any) => {
    const transactionData = {
      transaction_type: 'sell' as const,
      s_id: record.id,
      amount: Number(record.due_amount || 0),
      customer_name: record.customer_name, // Add customer_name
      type: 'MEDICAL_SALES', // Set payment type to MEDICAL_SALES
    };

    if (onCreateTransaction) {
      onCreateTransaction(transactionData);
    }
  };

  return useMemo(() => {
    const baseColumns: SmartDataTableProps['columns'] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'customer_info',
        title: 'Customer Info',
        dataIndex: 'customer_name',
        sorter: false,
        minWidth: 200,
        render: (customerName: any, record: any) => {
          const isClickable = record.patient;

          return (
            <div
              onClick={e => {
                handlePatientInfoClick(record, e);
              }}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                padding: '2px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
              }}
              onMouseEnter={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                }
              }}
              onMouseLeave={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
              title={isClickable ? 'Click to view patient details' : undefined}
            >
              {record.customer_name && (
                <>
                  <div style={{ fontWeight: 500, color: '#262626' }}>
                    {`${customerName || ''}`.trim() || '-'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    +{record.customer_phone || '-'}
                  </div>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'medicine_list',
        title: 'Medicine Items',
        dataIndex: 'sale_items',
        sorter: false,
        minWidth: 200,
        render: (sale_items: any[]) => {
          if (!sale_items || sale_items.length === 0) return '-';

          const tooltipContent = (
            <div style={{ maxWidth: '300px' }}>
              {sale_items.map((item, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '8px',
                    paddingBottom: '8px',
                    borderBottom:
                      index < sale_items.length - 1
                        ? '1px solid #f0f0f0'
                        : 'none',
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                    {item.medicine?.name || 'Unknown Medicine'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    <div>Quantity: {item.quantity}</div>
                    <div>Pack Size: {item.medicine?.pack_size || 'N/A'}</div>
                    <div>MRP: ₹{Number(item.mrp).toFixed(2)}</div>
                    <div>HSN: {item.medicine?.hsn || 'N/A'}</div>
                    <div>Discount: {item.discount}%</div>
                    <div>GST Rate: {item.gst_rate}%</div>
                    <div>Net Amount: ₹{Number(item.net_amount).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          );

          return (
            // <Tooltip title={tooltipContent}>
            <div style={{ cursor: 'pointer' }}>
              {sale_items.map((item, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: index < sale_items.length - 1 ? '4px' : '0',
                  }}
                >
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#262626',
                    }}
                  >
                    {item.medicine?.name || '-'} ({item.quantity})
                  </div>
                </div>
              ))}
            </div>
            // </Tooltip>
          );
        },
      },
      {
        key: 'financial_summary',
        title: 'Financial Summary',
        dataIndex: 'total_amount',
        sorter: false,
        minWidth: 160,
        render: (totalAmount: string, record: any) => {
          if (record._rowSpan === 0) return null;

          const subtotal = Number(record.subtotal) || 0;
          const discountAmount = Number(record.discount_amount) || 0;
          const taxAmount = Number(record.tax_amount) || 0;
          const billAmount = Number(record.bill_amount) || 0;
          const total = Number(totalAmount) || 0;
          const dueAmount = Number(record.due_amount) || 0;
          const clinicDue = Number(record.clinic_due_amount) || 0;
          const paidAmount = billAmount - dueAmount;

          return (
            <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
              {/* Main Total - Most Prominent */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2px',
                }}
              >
                <Text
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#262626',
                  }}
                >
                  Bill:
                </Text>
                <Text
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#52c41a',
                  }}
                >
                  ₹{Number(billAmount).toFixed(2)}
                </Text>
              </div>

              {/* Payment Status - Compact */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2px',
                }}
              >
                <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                  Paid/Due:
                </Text>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <Text
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#52c41a',
                    }}
                  >
                    ₹{Number(paidAmount).toFixed(2)}
                  </Text>
                  <Text style={{ fontSize: '10px', color: '#d9d9d9' }}>/</Text>
                  <Text
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: dueAmount > 0 ? '#ff4d4f' : '#52c41a',
                    }}
                  >
                    ₹{Number(dueAmount).toFixed(2)}
                  </Text>
                </div>
              </div>

              {/* Clinic Due Amount - Compact */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2px',
                }}
              >
                <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                  Clinic Due:
                </Text>
                <div
                  style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                  <Text
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: clinicDue > 0 ? '#52c41a' : '#8c8c8c',
                    }}
                  >
                    ₹{Number(clinicDue).toFixed(2)}
                  </Text>
                </div>
              </div>

              {/* Total Amount - Compact */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2px',
                }}
              >
                <Text
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#262626',
                  }}
                >
                  Total:
                </Text>
                <Text
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#52c41a',
                  }}
                >
                  ₹{Number(total).toFixed(2)}
                </Text>
              </div>
              

              {/* Payment Status Badge - Only when due */}
              {/* {dueAmount > 0 && (
                <div
                  style={{
                    marginTop: '2px',
                    padding: '1px 4px',
                    backgroundColor: '#fff2f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '3px',
                    textAlign: 'center',
                    width: 'fit-content',
                  }}
                >
                  <Text
                    style={{
                      fontSize: '9px',
                      color: '#ff4d4f',
                      fontWeight: 500,
                    }}
                  >
                    DUE
                  </Text>
                </div>
              )} */}
            </div>
          );
        },
        onCell: (record: any) => ({
          rowSpan: record._rowSpan || 1,
        }),
      },
      {
        key: 'payment_status',
        title: 'Status',
        dataIndex: 'payment_status',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...PAYMENT_STATUS_FILTER_OPTIONS],
        render: renderPaymentStatus,
      },
      {
        key: 'actions',
        title: 'Payment',
        dataIndex: 'actions',
        minWidth: 120,
        render: (value: any, record: any) => {
          // In Ant Design table render function, the second parameter is the record
          if (!record) return null;

          const dueAmount = Number(record.due_amount || 0);
          const canCreateTransaction = dueAmount > 0;

          if (!canCreateTransaction) return null;

          return (
            <Tooltip
              title={`Create Sale Transaction - ₹${dueAmount.toFixed(2)} due`}
            >
              <Button
                type="primary"
                size="small"
                icon={<DollarOutlined />}
                onClick={() => handleCreateTransaction(record)}
                style={{
                  fontSize: '11px',
                  height: '28px',
                  padding: '4px 8px',
                }}
              >
                Pay ₹{dueAmount.toFixed(2)}
              </Button>
            </Tooltip>
          );
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [renderPaymentStatus, userTimestampRender, onCreateTransaction, showCreatedAt]);
};
